import { useQuery, useMutation, useQueryClient } from 'react-query';
import { photoService, PhotoFilters } from '../services/photo.service';
import { PhotosResponse, UploadResponse } from '../types';

export function usePhotos(filters: PhotoFilters = {}) {
  return useQuery<PhotosResponse, Error>(
    ['photos', filters],
    () => photoService.getPhotos(filters),
    {
      keepPreviousData: true,
      staleTime: 30 * 1000, // 30 seconds - shorter for pagination
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
}

export function useUploadPhotos() {
  const queryClient = useQueryClient();

  return useMutation<UploadResponse, Error, File[]>(
    (files) => photoService.uploadPhotos(files),
    {
      onSuccess: () => {
        // Invalidate photos queries to refresh the list
        queryClient.invalidateQueries(['photos']);
      },
    }
  );
}

export function useUpdatePhotoTags() {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { photoId: number; tagIds: number[] }>(
    ({ photoId, tagIds }) => photoService.updatePhotoTags(photoId, tagIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['photos']);
      },
    }
  );
}

export function useBulkTagPhotos() {
  const queryClient = useQueryClient();

  return useMutation<
    any,
    Error,
    { photoIds: number[]; tagIds: number[]; action: 'add' | 'remove' | 'replace' }
  >(
    ({ photoIds, tagIds, action }) => photoService.bulkTagPhotos(photoIds, tagIds, action),
    {
      onSuccess: () => {
        // Invalidate both photos and tags queries to refresh counts
        queryClient.invalidateQueries(['photos']);
        queryClient.invalidateQueries(['tags']);
      },
    }
  );
}